/**
 * https://github.com/europace/baufismart-angebote-api
 *
 */
import { ILoanRequestAddSubmit } from './LoanRequest';
import LocalStorage from './LocalStorage';
import { IRequestError } from './RequestManager';
import RequestResponse from './RequestResponse';
import Config from './Config';
import PropApiRequest from './PropApiRequest';

const BASE_URL = Config.get('baseUrl').replace(/\/$/, ''); // without trailing slash
const useDnzProxy = true;
const isTestModus = Config.get('environment') !== 'prod';
// const API_URL = 'https://baufismart.api.europace.de';
const API_URL = useDnzProxy ? BASE_URL + '/europace/baufismart-angebote-api' : 'https://baufismart.api.europace.de';

/**
 * 
 * {
      "vorhaben": {
        "finanzbedarf": {
          "kaufpreis": 100000
        },
        "finanzierungswunsch": {
             "darlehensWuensche": [
               {
                 "annuitaetenDarlehen": {
                     "darlehensBetrag": 150000,
                     "provision": 1,
                     "tilgungsWunsch": {
                         "anfaenglicheTilgung": 2,
                         "volltilgerWennAnnuitaetenOderForward": false
                     },
                     "bereitstellungsZinsFreieZeitInMonaten": 2,
                     "sondertilgungOptionalJaehrlich": 100,
                     "zinsBindungInJahren": 10
                 }
               }

             ]
        }
      }
    }
 * 
 */

/**
 * Types for Request
 */
type IBaufiRequest = {
    vorhaben: IBaufiVorhaben; // required
    datenKontext: 'TEST_MODUS' | 'ECHT_GESCHAEFT';
    // bankverbindung?: any;
    externeVorgangsNummer?: string;
    // finanzierungsObjekt?: any;
    // haushalte?: any;
    // kundenBetreuer?: any;
    // leadTracking?: any;
    // tippGeber?: any;
    // vorgangsBearbeiter?: any;
    vorgangsNummer?: string;
};

type IBaufiVorhaben = {
    eigenleistungWennNeubauOderModernisierungsmassnahmen?: number;
    // externeBausparAngebote?: any[];
    finanzbedarf?: IBaufiFinanzbedarf;
    finanzierungswunsch?: IBaufiFinanzierungswunsch;
    id?: string;
    nachfinanzierung?: boolean;
    // nachrangigeExterneDarlehen?: any[];
    verwendungszweck?: 'ANSCHLUSSFINANZIERUNG' | 'KAUF' | 'KAUF_NEUBAU_VOM_BAUTRAEGER' | 'MODERNISIERUNG_UMBAU_ANBAU' | 'NEUBAU' | 'KAPITALBESCHAFFUNG';
};

type IBaufiFinanzbedarf = {
    aussenAnlagen?: number;
    bauNebenkosten?: number;
    beratungsHonorar?: number;
    bereitsBeglichen?: number;
    erschliessung?: number;
    grunderwerbsteuer?: number;
    grundstueckBereitsBezahlt?: boolean;
    grundstuecksKaufpreis?: number;
    herstellung?: number; // inklusive Eigenleistungen
    kapitalBeschaffung?: number;
    kapitalWirdBeschafft?: boolean;
    kaufpreis?: number; // inklusive Eigenleistungen
    maklergebuehr?: number; // Nebenkosten
    mobiliar?: number;
    modernisieren?: boolean;
    modernisierung?: number; // inklusive Eigenleistungen;
    notargebuehr?: number; // Nebenkosten;
    renovierung?: number;
    sonstigeKosten?: number;
    zusaetzlichesKapital?: number;
};

type IBaufiFinanzierungswunsch = {
    // bausparWuensche?: IBaufiBausparWunsch[];
    darlehensWuensche?: IBaufiDarlehensWunsch[];
    id?: string;
};

type IBaufiDarlehensWunsch = {
    annuitaetenDarlehen?: IBaufiAnnuitaetenDarlehensWunsch;
    // forwardDarlehen?: ForwardDarlehensWunsch;
    id?: string;
    // kfwDarlehen?: KfwDarlehensWunsch;
    // privatDarlehen?: PrivatDarlehensWunsch;
    // regionalFoerderDarlehen?: RegionalFoerderDarlehensWunsch;
    // variablesDarlehen?: VariablesDarlehensWunsch;
    // zwischenFinanzierung?: ZwischenFinanzierungsWunsch;
};

type IBaufiAnnuitaetenDarlehensWunsch = {
    auszahlungsZeitpunkt?: string; //($date)
    bereitstellungsZinsFreieZeitInMonaten?: number; // integer($int32)
    darlehensBetrag?: number; // Der Darlehensbetrag muss ein positiver Wert zwischen 1000 und 10 Milliarden sein.
    provision?: number;
    riesterGefoerdert?: boolean;
    riesterGefoerdertePerson?: string;
    sondertilgungOptionalJaehrlich?: number;
    tilgungsWunsch?: IBaufiTilgungsWunsch;
    zinsBindungInJahren?: number; // integer($int32) Wert muss größer 0 sein.
};

type IBaufiTilgungsWunsch = {
    anfaenglicheTilgung?: number; //Darf nur angegeben werden, wenn 'volltilgerWennAnnuitaetenOderForward' und 'rate' nicht gesetzt sind. Wert muss größer/gleich 0 sein
    ausgesetztBerechnet?: boolean; //ausgesetztBerechnet und ausgesetztEigenesAngebot dürfen nicht beide true sein
    ausgesetztEigenesAngebot?: boolean; // ausgesetztBerechnet und ausgesetztEigenesAngebot dürfen nicht beide true sein
    // bausparWunsch?:BausparWunsch{...}Jump to definition
    rate?: number; //Darf nur angegeben werden, wenn 'anfaenglicheTilgung' und 'volltilgerWennAnnuitaetenOderForward' nicht gesetzt sind. Wert muss größer 0 sein
    tilgungsBeginn?: string; // ($date)
    tilgungsErsatzProduktId?: string;
    volltilgerWennAnnuitaetenOderForward?: boolean; // Darf nur angegeben werden, wenn 'anfaenglicheTilgung' und 'rate' nicht gesetzt sind.
};

/**
 * Types for Response
 */
type IBaufiResponse = {
    _links: IBaufiLinks;
    ergebnisse: IBaufiErgebnis[];
    ermittlungsId: string;
};

type IBaufiLinks = {
    [name: string]: IBaufiLink;
};

type IBaufiLink = {
    deprecation: string;
    href: string;
    hreflang: string;
    media: string;
    name: string;
    profile: string;
    // rel:LinkRelation;//{...}Jump to definition
    // template:UriTemplate;//{...}Jump to definition
    templated: boolean;
    title: string;
    type: string;
};

type IBaufiErgebnis = {
    _links: IBaufiLinks;
    annahmeFrist: string; //($date-time)
    anpassungsStatus: 'NICHT_ANGEPASST' | 'ANGEPASST' | 'ALTERNATIV';
    // bausparAngebote: TODO;
    beleihung: IBaufiBeleihung[];
    darlehen: IBaufiDarlehen[];
    darlehensSumme: number;
    effektivZins: number;
    erzeugtAm: string; //($date-time)
    kennung: string;
    machbarkeit: 'MACHBAR' | 'UNTER_VORBEHALT' | 'NICHT_MACHBAR';
    meldungen: IBaufiMeldungen;
    sollZins: number;
};

type IBaufiBeleihung = {
    auslauf: number;
    produktAnbieter: IBaufiProduktAnbieter; // {...}Jump to definition
    summe: number;
};

type IBaufiProduktAnbieter = {
    _links: IBaufiLinks; // {...}
    name: string;
    partnerId: string;
    produktAnbieterId: string;
};

type IBaufiDarlehen = {
    auszahlungsBetrag: number; //Der Auszahlungsbetrag ist der Nettodarlehensbetrag

    auszahlungsDatum: string; //($date)
    bereitstellung: IBaufiBereitstellung; //{...}Jump to definition
    darlehensBetrag: number;
    effektivZins: number;
    effektivZinsRelevanteKosten: IBaufiEffektivZinsRelevanteKosten; // {...}Jump to definition
    finanzierenderProduktAnbieter: IBaufiProduktAnbieter; // {...}Jump to definition
    gesamtkosten: number;
    gesamtlaufzeitInMonaten: number; //integer($int32)
    id: string;
    produktAnbieter: IBaufiProduktAnbieter; // {...}Jump to definition
    rateMonatlich: number;
    sollZins: number;
    tilgung: IBaufiTilgung; // {...}Jump to definition
    typ:
    | 'ANNUITAETEN_DARLEHEN'
    | 'FORWARD_DARLEHEN'
    | 'REGIONAL_FOERDER_DARLEHEN'
    | 'KFW_DARLEHEN'
    | 'PRIVAT_DARLEHEN'
    | 'ZWISCHEN_FINANZIERUNG'
    | 'VARIABLES_DARLEHEN'
    | 'BAUSPARVERTRAG';
    zinsBindung: IBaufiZinsBindung; // {...}Jump to definition
    zinsZahlungsBeginnAm: string; //($date)
};

type IBaufiBereitstellung = {
    bereitstellungsZins: number;
    bereitstellungsZinsfreieZeitInMonaten: number; //integer($int32)
};

type IBaufiEffektivZinsRelevanteKosten = {
    beratungsHonorar: number;
    grundbuchKosten: number;
    sonstigeKosten: number;
    tilgungsErsatzProduktKosten: number;
    wohnGebaeudeVersicherungsKosten: number;
    zusatzSicherheitsKosten: number;
};

type IBaufiTilgung = {
    anfaenglicheTilgung: number;
    sonderTilgungJaehrlich: number;
    tilgungsBeginn: string; //($date)
};

type IBaufiZinsBindung = {
    endetAm: string; //($date)
    jahre: number; //integer($int32)
    monate: number; // integer($int32)
    restschuldNachZinsBindungsEnde: number;
};

type IBaufiMeldungen = {
    _links: IBaufiLinks;
    meldungen: IBaufiMeldung[];
};

type IBaufiMeldung = {
    bereichsZuordnung: 'ANTRAGSTELLER' | 'OBJEKT' | 'VORHABEN' | 'ALTERNATIV' | 'ANPASSUNG' | 'UNBERUECKSICHTIGTE_ANGABE';
    code: string;
    meldungsKategorie:
    | 'AUSZAHLUNGS_VORAUSSETZUNG'
    | 'MACHBAR'
    | 'MACHBARKEITS_HINWEIS'
    | 'ANPASSUNG_KUNDENWUNSCH'
    | 'ANPASSUNG_VERTRIEBSWUNSCH'
    | 'UNBERUECKSICHTIGTE_ANGABE'
    | 'KONDITIONEN_UNTER_VORBEHALT_VOLLSTAENDIGER_DATEN'
    | 'KONDITIONEN_UNTER_VORBEHALT_EXTERNER_SCHNITTSTELLEN'
    | 'MACHBARKEIT_UNTER_VORBEHALT_VOLLSTAENDIGER_DATEN'
    | 'MACHBARKEIT_UNTER_VORBEHALT_EXTERNER_SCHNITTSTELLEN'
    | 'MACHBAR_UNTER_VORBEHALT_PRODUZENT'
    | 'NICHT_MACHBAR'
    | 'NICHT_ANBIETBAR';
    produktAnbieterId: string;
    text: string;
};

type IBaufiTokenResponse = {
    access_token: string;
    scope: string; // 'privatkredit:angebot:ermitteln baufinanzierung:produktanbieter:lesen baufinanzierung:produkt:lesen baufinanzierung:angebot:ermitteln';
    token_type: string; // 'Bearer';
    expires_in: number; // 3600;
};

/**
 * view type
 **/
export type ILoanOffer = {
    /**
     * Sollzins in Prozent
     */
    sollZins: number;
    /**
     * Effektivzins in Prozent
     */
    effektivZins: number;
    /**
     * monatliche Rate in Euro
     */
    monatlicheRate: number;
    /**
     * Restschuld zum Ende der Sollzinsbindung in Euro
     */
    restSchuld: number;
    /**
     * zu zahlender Gesamtbetrag in Euro
     */
    gesamtBetrag: number;
    /**
     * Laufzeit bis zur vollständigen Rückzahlung in Monaten
     */
    gesamtLaufzeit: number;

    /**
     * Name der finanzierenden Bank
     */
    bankName: string;

    /**
     * Logo der finanzierenden Bank
     */
    bankLogo: string;

    /**
     * Machbarkeit lt. Schnittstelle
     */
    machbarkeit: IBaufiErgebnis['machbarkeit'];

    /**
     * darlehens id aus Schnittstelle
     */
    baufiId: string;
};

class BaufiSmartApi {
    protected token: string = null;
    protected tokenExpires: number = null;
    protected LoadingCredentialsPromise = null;

    public getBanks = (data: ILoanRequestAddSubmit): Promise<ILoanOffer[]> => {
        return this.getToken().then((token) => {
            const baufiData = this.loan2baufi(data);
            return this.request('POST', API_URL + '/v2/ergebnisliste/ermittlung', baufiData).then((res) => {
                const resData = res.getData();
                const loanData = this.baufi2LoanOffer(resData);
                return Promise.resolve(loanData);
            });
        });
    };

    protected baufi2LoanOffer = (data: IBaufiResponse): ILoanOffer[] => {
        const loanData: ILoanOffer[] = [];

        data.ergebnisse.map((erg) => {
            if (erg.darlehen[0] && !erg.darlehen[1]) {
                // only offers with one darlehen
                const dar = erg.darlehen[0];
                loanData.push({
                    sollZins: dar.sollZins,
                    effektivZins: dar.effektivZins,
                    monatlicheRate: dar.rateMonatlich,
                    restSchuld: dar.zinsBindung?.restschuldNachZinsBindungsEnde,
                    gesamtBetrag: dar.gesamtkosten,
                    gesamtLaufzeit: dar.gesamtlaufzeitInMonaten,
                    bankName: dar.produktAnbieter?.name,
                    bankLogo: dar.produktAnbieter?._links?.logo?.href,
                    machbarkeit: erg.machbarkeit,
                    baufiId: dar.id,
                });
            }
        });

        return (
            loanData
                /* filter 'NICHT_MACHBAR' */
                .filter((d) => d.machbarkeit !== 'NICHT_MACHBAR')
                /* filter duplicates */
                .filter((thing, index, self) => index === self.findIndex((t) => JSON.stringify(t) === JSON.stringify(thing)))
                /* sort by effektiv Zins */
                .sort((a, b) => a.effektivZins - b.effektivZins)
                /* TOP 10 */
                .slice(0, 10)
        );
    };

    protected loan2baufi = (data: ILoanRequestAddSubmit): IBaufiRequest => {
        const d = data.loanCalculator || {};
        return {
            vorhaben: {
                finanzbedarf: {
                    grunderwerbsteuer: (d.stampDutyPercentage * d.price) / 100,
                    kaufpreis: d.price,
                    maklergebuehr: (d.findersFeePercentage * d.price) / 100,
                    notargebuehr: ((d.notarialCostsPercentage + d.landRegisterCostsPercentage) * d.price) / 100,
                    // sonstigeKosten: (d.landRegisterCostsPercentage * d.price) / 100,
                },
                finanzierungswunsch: {
                    darlehensWuensche: [
                        {
                            annuitaetenDarlehen: {
                                //   auszahlungsZeitpunkt:
                                darlehensBetrag: d.price + (d.closingCostsPercentage * d.price) / 100 - d.equity,
                                tilgungsWunsch: {
                                    anfaenglicheTilgung: d.amortizationRatePercentage,
                                },
                                zinsBindungInJahren: d.fixedInterestRateYears,
                            },
                        },
                    ],
                },
            },
            datenKontext: isTestModus ? 'TEST_MODUS' : 'ECHT_GESCHAEFT',
        };
    };

    protected getToken = (forceRenew: boolean = false): Promise<string> => {
        if (!forceRenew && this.token && this.tokenExpires > Date.now()) {
            // token from class
            return Promise.resolve(this.token);
        } else {
            // token from local storage
            const savedToken: { token: string; tokenExpires: number } = LocalStorage.getItem('BaufiSmartApiToken');
            if (savedToken?.tokenExpires - 100 > Date.now()) {
                this.token = savedToken.token;
                this.tokenExpires = savedToken.tokenExpires;
                return Promise.resolve(savedToken.token);
            } else {
                // token from API
                return this.getTokenFromApi(forceRenew).then((res) => {
                    const data: IBaufiTokenResponse = res.getData()['baufismart-angebote-api'];
                    this.token = data.access_token;
                    this.tokenExpires = Date.now() + data.expires_in * 1000;
                    LocalStorage.setItem('BaufiSmartApiToken', { token: this.token, tokenExpires: this.tokenExpires });
                    return Promise.resolve(this.token);
                });
            }
        }
    };

    protected getTokenFromApi = (forceRenew: boolean = false) => {
        if (!this.LoadingCredentialsPromise) {
            this.LoadingCredentialsPromise = PropApiRequest.getRequest(
                BASE_URL + '/prop-api/europace/credentials?api[]=baufismart-angebote-api' + (forceRenew ? '&force-renew=1' : '')
            )
                .then((res) => {
                    this.LoadingCredentialsPromise = null;
                    return res;
                })
                .catch((e) => {
                    console.error(e);
                    this.LoadingCredentialsPromise = null;
                    return Promise.reject(e);
                });
        }
        return this.LoadingCredentialsPromise;
    };

    protected request = (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS', url: string, data?: any) => {
        return new Promise<RequestResponse>((resolve: (resp: RequestResponse) => void, reject: (resp: IRequestError) => void) => {
            const xhr = new XMLHttpRequest();
            xhr.open(method, url);
            xhr.onerror = (e: any) => {
                const resp = new RequestResponse(xhr.status, url);
                console.error('XHR Error:', xhr);
                let data = null;
                try {
                    data = JSON.parse(xhr.responseText);
                } catch (e) {
                    console.error(e);
                }
                reject({ ...resp, responseData: data });
            };

            xhr.ontimeout = () => {
                console.error('timeout for request', url);
            }

            xhr.onreadystatechange = () => {
                if (xhr.readyState === XMLHttpRequest.DONE) {
                    const resp = new RequestResponse(xhr.status, url);
                    if (xhr.status >= 200 && xhr.status <= 299) {
                        try {
                            resp.setData(JSON.parse(xhr.responseText));
                        } catch (e) {
                            resp.setData(xhr.responseText);
                        }
                        resolve(resp);
                    } else {
                        console.error('Request error', xhr.status, resp);
                        let data = null;
                        try {
                            data = JSON.parse(xhr.responseText);
                        } catch (e) {
                            console.error(e);
                        }
                        reject({ ...resp, responseData: data });
                    }
                }
            };

            xhr.setRequestHeader(useDnzProxy ? 'x-authorization' : 'authorization', `Bearer ${this.token}`);
            xhr.setRequestHeader('cache-control', `no-cache`);
            xhr.setRequestHeader('content-type', `application/json`);
            xhr.setRequestHeader('X-TraceId', `dnz_${Date.now()}`);
            xhr.timeout = 30000;
            xhr.send(data ? JSON.stringify(data) : null);
        });
    };
}

export default new BaufiSmartApi();
